import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { onIdTokenChanged } from "firebase/auth";

import { CENTRAL_ROLES, ORG_ID, ROLES } from "../constants";
import { hasKistenAccess, normalizeClaimRoles } from "../utils/centralRoles";
import {
  auth,
  signInWithCustomToken,
  signOut,
} from "../services/auth.js";
import { firebaseProjectId } from "../services/FireBase.js";
import {
  getVerdonkAppsNoticeKey,
  requestKistenToken,
  verdonkAppsSsoEnabled,
} from "../services/verdonkAppsAuth.js";

const AuthContext = createContext(null);
const DIRECT_SCANNER_ROLES = Object.freeze([
  ROLES.INSCANNER,
  ROLES.INSCANNER_PLUS,
  ROLES.UITSCANNER,
]);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [authError, setAuthError] = useState(null);
  const [ssoChecking, setSsoChecking] = useState(false);
  const [ssoNoticeKey, setSsoNoticeKey] = useState(null);
  const authLoadSeq = useRef(0);
  const ssoAttempted = useRef(false);

  const resolvePrincipal = useCallback(async (user, tokenResult, sequence) => {
    const claimRoles = normalizeClaimRoles(tokenResult?.claims);
    const legacyClaimRole = tokenResult?.claims?.role || null;
    const hasCentralAccess = hasKistenAccess(claimRoles);
    const hasDirectScannerClaims =
      tokenResult?.claims?.staff === true &&
      tokenResult?.claims?.orgId === ORG_ID &&
      DIRECT_SCANNER_ROLES.includes(legacyClaimRole);
    setRoles(claimRoles);

    if (user.isAnonymous) {
      setCurrentUser(null);
      setAuthError({
        code: "kisten/anonymous-disabled",
        message: "Anoniem inloggen wordt niet meer ondersteund.",
      });
      return;
    }

    if (sequence !== authLoadSeq.current) return;

    const hasDirectScannerAccess =
      !hasCentralAccess && hasDirectScannerClaims;

    if (!hasCentralAccess && !hasDirectScannerAccess) {
      setCurrentUser(null);
      setAuthError({
        code: "kisten/access-denied",
        message: "Je account heeft geen toegang tot Kisten.",
      });
      return;
    }

    const centralWorkRole = claimRoles.includes(CENTRAL_ROLES.KISTEN_ADMIN)
      ? ROLES.ADMIN
      : claimRoles.includes(CENTRAL_ROLES.KISTEN_PLANNING)
        ? ROLES.PLANNING
        : claimRoles.includes(CENTRAL_ROLES.KISTEN_SUPERVISOR)
          ? ROLES.SUPERVISOR
          : null;
    const role = centralWorkRole ||
      (hasDirectScannerAccess ? legacyClaimRole : null);
    const displayName =
      user.displayName ||
      user.email ||
      "Onbekend";

    if (!role) {
      setCurrentUser(null);
      setAuthError({
        code: "kisten/operational-role-missing",
        message: "Je Kisten-werkrol is nog niet ingesteld.",
      });
      return;
    }

    setAuthError(null);
    setSsoNoticeKey(null);
    setCurrentUser({ uid: user.uid, displayName, role, roles: claimRoles });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const unsubscribeAuth = onIdTokenChanged(auth, async (user) => {
      const sequence = authLoadSeq.current + 1;
      authLoadSeq.current = sequence;
      setFirebaseUser(user);

      if (!user) {
        if (cancelled) return;
        setCurrentUser(null);
        setRoles([]);
        setAuthError(null);

        if (verdonkAppsSsoEnabled && !ssoAttempted.current) {
          ssoAttempted.current = true;
          setSsoChecking(true);
          setProfileLoaded(false);
          try {
            const tokenResponse = await requestKistenToken({
              expectedProjectId: firebaseProjectId,
            });
            if (cancelled || sequence !== authLoadSeq.current) return;
            await signInWithCustomToken(auth, tokenResponse.token);
            return;
          } catch (error) {
            if (!cancelled && sequence === authLoadSeq.current) {
              setSsoNoticeKey(getVerdonkAppsNoticeKey(error));
            }
          } finally {
            if (!cancelled && sequence === authLoadSeq.current) {
              setSsoChecking(false);
              setProfileLoaded(true);
            }
          }
          return;
        }

        setProfileLoaded(true);
        return;
      }

      setSsoChecking(false);
      setProfileLoaded(false);
      try {
        const tokenResult = await user.getIdTokenResult();
        if (!cancelled) await resolvePrincipal(user, tokenResult, sequence);
      } catch (error) {
        if (!cancelled && sequence === authLoadSeq.current) {
          setCurrentUser(null);
          setRoles([]);
          setAuthError({
            code: error?.code || "auth/permissions-unavailable",
            message: "De Kisten-rechten konden niet worden geladen.",
          });
        }
      } finally {
        if (!cancelled && sequence === authLoadSeq.current) {
          setProfileLoaded(true);
        }
      }
    });

    return () => {
      cancelled = true;
      unsubscribeAuth();
    };
  }, [resolvePrincipal]);

  const refreshPermissions = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return [];

    const sequence = authLoadSeq.current + 1;
    authLoadSeq.current = sequence;
    setProfileLoaded(false);
    setAuthError(null);
    try {
      await user.getIdToken(true);
      const tokenResult = await user.getIdTokenResult();
      await resolvePrincipal(user, tokenResult, sequence);
      return normalizeClaimRoles(tokenResult?.claims);
    } catch (error) {
      setAuthError({
        code: error?.code || "auth/permissions-refresh-failed",
        message: "De Kisten-rechten konden niet worden vernieuwd.",
      });
      throw error;
    } finally {
      if (sequence === authLoadSeq.current) setProfileLoaded(true);
    }
  }, [resolvePrincipal]);

  const logout = useCallback(() => signOut(auth), []);
  const hasRole = useCallback((role) => roles.includes(role), [roles]);
  const hasAnyRole = useCallback(
    (requiredRoles) => requiredRoles.some((role) => roles.includes(role)),
    [roles]
  );

  const permissions = useMemo(() => {
    const role = currentUser?.role;
    const hasSupervisorAccess =
      role === ROLES.SUPERVISOR || role === ROLES.ADMIN;
    return {
      role,
      isAdmin: role === ROLES.ADMIN,
      canScan:
        !!role &&
        (role === ROLES.INSCANNER ||
          role === ROLES.INSCANNER_PLUS ||
          hasSupervisorAccess),
      canUitscan:
        !!role && (role === ROLES.UITSCANNER || hasSupervisorAccess),
      canEdit: !!role && hasSupervisorAccess,
      isPlanning: !!role && role === ROLES.PLANNING,
    };
  }, [currentUser?.role]);

  const value = useMemo(
    () => ({
      currentUser,
      user: firebaseUser,
      roles,
      loading: !profileLoaded,
      error: authError,
      ssoChecking,
      ssoNoticeKey,
      hasRole,
      hasAnyRole,
      refreshPermissions,
      logout,
      profileLoaded,
      firebaseUser,
      ...permissions,
    }),
    [
      currentUser,
      roles,
      authError,
      ssoChecking,
      ssoNoticeKey,
      profileLoaded,
      firebaseUser,
      permissions,
      hasRole,
      hasAnyRole,
      refreshPermissions,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return context;
}
